import { getSupabaseClient } from '../supabase-client';
import { readSupabaseEnv } from '../../lib/env';
import { reportError } from '../../lib/monitoring';
import type { SolAnswerResult, SolMemoryCard } from './sol-contract';

/**
 * Asking Sol. M9 / S09-04.
 *
 * **ONE AUTHENTICATED CALL TO CREWLINK'S OWN ENDPOINT, AND NOTHING ELSE** — the
 * same shape `organize-client.ts` established and for the same reason (D-012):
 * no provider is named here, no model is chosen here, no prompt exists here,
 * and no key is reachable from here. The vendor is deliberately unnamed even in
 * a comment, so `check:secrets:build` can assert the strong property — the name
 * appears in no client source and in no built artifact.
 *
 * **THE REQUEST CARRIES A QUESTION AND A PROJECT ID.** Not evidence, not scope,
 * not instructions, not a model, not a temperature. The endpoint REFUSES any
 * other field rather than ignoring it, so this shape is enforced on both sides.
 *
 * **NO RETRY LIVES HERE.** Whatever transient retry is permitted belongs to the
 * server, which is the only side that knows what it costs. A browser retry loop
 * on a paid endpoint is how one tap becomes four.
 */

export const SOL_ASK_FUNCTION_NAME = 'sol-ask';

/**
 * What the call did. **`aborted` is a first-class outcome, not an error**: a
 * superseded question is the normal case, and reporting it as a failure would
 * make an ordinary retype look like a fault.
 */
export type SolAskOutcome =
  | {
      readonly status: 'answered';
      readonly result: SolAnswerResult;
      /** The conversation this turn belongs to, for the next question to continue. */
      readonly conversationId: string | null;
      /**
       * An AUTOMATIC memory Crewlink stored from his own words this turn (M11 /
       * D-040), or null. **Visible by contract** — the screen shows it with a
       * Forget control; nothing automatic is ever kept silently. Additive, like
       * `speech` and `proposal` on the result: absent from a server (or a
       * harness) that predates it.
       */
      readonly memoryNoted?: SolMemoryCard | null;
    }
  | { readonly status: 'signed_out' }
  | { readonly status: 'unavailable' }
  | { readonly status: 'aborted' };

export interface SolAskRequest {
  readonly question: string;
  /** The Project on screen, or null. **Context, never a permission.** */
  readonly currentProjectId: string | null;
  /**
   * The conversation this question continues, or null to begin one (M11).
   *
   * **AN IDENTIFIER, NOT A PERMISSION, AND NOT MEMORY EITHER.** The browser
   * sends only the id; what that conversation contains is read on the server,
   * as the signed-in caller, under RLS. No earlier question, answer, cursor or
   * filter travels from here — the browser could not be trusted with them, and
   * does not have them.
   */
  readonly conversationId?: string | null;
  readonly signal?: AbortSignal;
}

function isAbort(caught: unknown): boolean {
  return caught instanceof Error && caught.name === 'AbortError';
}

/** Shape-checked, never assumed: an id, a kind, an origin and the words. */
function isMemoryCard(value: unknown): value is SolMemoryCard {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === 'string' &&
    (record.kind === 'preference' ||
      record.kind === 'instruction' ||
      record.kind === 'business_context') &&
    (record.origin === 'explicit' || record.origin === 'automatic') &&
    typeof record.content === 'string'
  );
}

export async function askSol(request: SolAskRequest): Promise<SolAskOutcome> {
  const env = readSupabaseEnv();
  const client = getSupabaseClient();
  if (!env || !client) return { status: 'unavailable' };

  try {
    const { data, error } = await client.auth.getSession();
    if (error) return { status: 'unavailable' };
    const accessToken = data.session?.access_token;
    if (!accessToken) return { status: 'signed_out' };

    const response = await fetch(
      `${env.url.replace(/\/+$/, '')}/functions/v1/${SOL_ASK_FUNCTION_NAME}`,
      {
        method: 'POST',
        headers: {
          apikey: env.anonKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: request.question,
          currentProjectId: request.currentProjectId,
          conversationId: request.conversationId ?? null,
        }),
        ...(request.signal ? { signal: request.signal } : {}),
      },
    );

    if (response.status === 401) return { status: 'signed_out' };
    if (!response.ok) return { status: 'unavailable' };

    const payload = (await response.json()) as { kind?: unknown; conversationId?: unknown };
    // **SHAPE IS CHECKED, NOT ASSUMED.** A body without a `kind` is not an
    // answer, and rendering one would put an empty answer card on the screen
    // with nothing in it.
    if (typeof payload.kind !== 'string') return { status: 'unavailable' };
    const conversationId = (payload as { conversationId?: unknown }).conversationId;
    const noted = (payload as { memoryNoted?: unknown }).memoryNoted;
    return {
      status: 'answered',
      result: payload as unknown as SolAnswerResult,
      conversationId: typeof conversationId === 'string' ? conversationId : null,
      memoryNoted: isMemoryCard(noted) ? noted : null,
    };
  } catch (caught) {
    // An aborted request is a superseded question, not a fault. It is never
    // reported, because a monitoring channel full of ordinary retypes is a
    // monitoring channel nobody reads.
    if (isAbort(caught)) return { status: 'aborted' };
    reportError(caught, 'sol.ask');
    return { status: 'unavailable' };
  }
}
